import React, { useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import LogoFull from './assets/Logo.png';
import MiniLogo from './assets/MiniLogo.png';
import { getRandomDemandLines } from './utils/testPool';
import parseDemandLines from './utils/parseDemandLines';

const MODEL_OPTIONS = [
  {
    id: 'OriginalCPU',
    label: 'Original CPU (TF-IDF + KNN)',
    envKey: 'REACT_APP_MODEL_ORIGINALCPU_URL',
    defaultUrl: 'http://localhost:8013',
  },
  {
    id: 'WebSearcher',
    label: 'Web Search (Google + EMAS + TME + ELKOV)',
    envKey: 'REACT_APP_MODEL_WEBSEARCHER_URL',
    defaultUrl: 'http://localhost:8413',
  },
  {
    id: 'WebSearcher_Vector',
    label: 'Web Search + Vector Reranking',
    envKey: 'REACT_APP_MODEL_WEBSEARCHER_VECTOR_URL',
    defaultUrl: 'http://localhost:8513',
  },
];

const sanitizeBaseUrl = (value) => (value || '').trim().replace(/\/$/, '');

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '0.0.0.0']);

const isLocalHostname = (hostname) => {
  if (!hostname) {
    return true;
  }
  const lower = hostname.toLowerCase();
  return LOCAL_HOSTNAMES.has(lower) || lower.endsWith('.local');
};

const mergeOrigin = (url, origin) => {
  const safeUrl = sanitizeBaseUrl(url);
  const safeOrigin = sanitizeBaseUrl(origin);
  if (!safeUrl) {
    return '';
  }
  if (!safeOrigin) {
    return safeUrl;
  }
  try {
    const parsed = new URL(safeUrl);
    const originParsed = new URL(safeOrigin);
    parsed.protocol = originParsed.protocol || parsed.protocol;
    parsed.hostname = originParsed.hostname;
    parsed.port = originParsed.port;
    return sanitizeBaseUrl(parsed.toString());
  } catch (err) {
    // If base URL is invalid, fall back to the origin itself.
    return safeOrigin;
  }
};

const REMOTE_PREFIX = {};

const applyRemotePrefix = (url, modelId) => {
  if (!modelId || !REMOTE_PREFIX[modelId]) {
    return url;
  }
  try {
    const parsed = new URL(url);
    if (isLocalHostname(parsed.hostname)) {
      return url;
    }
    const prefix = REMOTE_PREFIX[modelId];
    if (!parsed.pathname || parsed.pathname === '/' || parsed.pathname === '') {
      parsed.pathname = prefix;
    }
    return sanitizeBaseUrl(parsed.toString());
  } catch (err) {
    return url;
  }
};

const resolveUrlForEnvironment = (rawUrl, modelId) => {
  const base = sanitizeBaseUrl(rawUrl);
  if (!base) {
    return '';
  }

  const explicitOrigin = sanitizeBaseUrl(process.env.REACT_APP_PUBLIC_API_ORIGIN || process.env.REACT_APP_API_ORIGIN || '');
  if (explicitOrigin) {
    return applyRemotePrefix(mergeOrigin(base, explicitOrigin), modelId);
  }

  if (typeof window !== 'undefined') {
    const { protocol, host, hostname } = window.location;
    const windowOrigin = sanitizeBaseUrl(`${protocol}//${host}`);
    if (windowOrigin && !isLocalHostname(hostname)) {
      try {
        const parsed = new URL(base);
        if (isLocalHostname(parsed.hostname)) {
          return applyRemotePrefix(mergeOrigin(base, windowOrigin), modelId);
        }
        return applyRemotePrefix(base, modelId);
      } catch (err) {
        return applyRemotePrefix(windowOrigin, modelId);
      }
    }
  }

  return applyRemotePrefix(base, modelId);
};


const escapeCsvValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const str = String(value);
  if (str.includes('"') || str.includes(',') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsv = (columns, rows) => {
  const header = columns.map((col) => escapeCsvValue(col.header));
  const lines = rows.map((row) =>
    columns
      .map((col) => {
        const value = typeof col.accessor === 'function' ? col.accessor(row) : row[col.key];
        return escapeCsvValue(value);
      })
      .join(',')
  );
  return [header.join(','), ...lines].join('\n');
};

const triggerCsvDownload = (fileName, csvContent) => {
  if (typeof window === 'undefined') {
    return;
  }
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DIRECT_CODE_HITS = new Set(['sap', 'ean']);

const computeCandidateConfidence = (candidate) => {
  if (!candidate || candidate.score === undefined || candidate.score === null) {
    return null;
  }
  if (candidate.code_match_type && DIRECT_CODE_HITS.has(candidate.code_match_type)) {
    return 100;
  }
  const rawScore = typeof candidate.score === 'number' ? candidate.score : Number(candidate.score);
  if (Number.isNaN(rawScore)) {
    return null;
  }
  const bounded = Math.max(0, Math.min(rawScore, 1));
  return Math.round(bounded * 100);
};

const buildCandidateBadges = (candidate) => {
  if (!candidate) {
    return [];
  }
  const badges = [];
  if (candidate.code_match_type === 'sap') {
    badges.push({ key: 'direct', className: 'badge direct-code', label: 'Přímý zásah SAP' });
  } else if (candidate.code_match_type === 'ean') {
    badges.push({ key: 'direct', className: 'badge direct-code', label: 'Přímý zásah EAN' });
  }
  const confidence = computeCandidateConfidence(candidate);
  if (confidence !== null) {
    badges.push({ key: 'confidence', className: 'badge score', label: `Jistota ${confidence}%` });
  }
  return badges;
};

const formatCandidate = (candidate) => {
  if (!candidate) {
    return '';
  }
  const parts = [candidate.sap || ''];
  if (candidate.name) {
    parts.push(candidate.name);
  }
  const confidence = computeCandidateConfidence(candidate);
  if (confidence !== null) {
    parts.push(`confidence ${confidence}%`);
  }
  if (candidate.ean) {
    parts.push(`EAN ${candidate.ean}`);
  }
  return parts.filter(Boolean).join(' | ');
};

const formatDateTag = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    '_',
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
  ].join('');
};

const findNextLineId = (lineItems, currentId) => {
  if (!lineItems.length) {
    return null;
  }
  const unresolved = lineItems.filter((line) => line.status === 'pending');
  if (!unresolved.length) {
    return currentId;
  }
  const currentIndex = lineItems.findIndex((line) => line.id === currentId);
  const searchOrder = [
    ...lineItems.slice(currentIndex + 1),
    ...lineItems.slice(0, currentIndex + 1),
  ];
  const next = searchOrder.find((line) => line.status === 'pending');
  return next ? next.id : currentId;
};

const App = () => {
  const initialUrls = useMemo(() => {
    const entries = MODEL_OPTIONS.map((model) => {
      const envValue = process.env[model.envKey];
      return [model.id, resolveUrlForEnvironment(envValue || model.defaultUrl, model.id)];
    });
    return Object.fromEntries(entries);
  }, []);

  const [modelUrls, setModelUrls] = useState(initialUrls);
  const [selectedModelId, setSelectedModelId] = useState(MODEL_OPTIONS[0].id);
  const [statuses, setStatuses] = useState({});
  const [inputText, setInputText] = useState('');
  const [useLLM, setUseLLM] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressCount, setProgressCount] = useState({ total: 0, completed: 0 });
  const [error, setError] = useState(null);
  const [lines, setLines] = useState([]);
  const [activeLineId, setActiveLineId] = useState(null);
  const [manualOverrides, setManualOverrides] = useState([]);
  const [finalizeMessage, setFinalizeMessage] = useState('');
  const [slangForm, setSlangForm] = useState({ slang: '', canonical: '', notes: '' });
  const [slangStatus, setSlangStatus] = useState({ message: '', error: false });
  const [slangSubmitting, setSlangSubmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const activeModel = MODEL_OPTIONS.find((m) => m.id === selectedModelId);
  const activeBaseUrl = sanitizeBaseUrl(modelUrls[selectedModelId]);

  const updateModelUrl = (modelId, value) => {
    setModelUrls((prev) => ({ ...prev, [modelId]: resolveUrlForEnvironment(value, modelId) }));
  };

  const checkStatus = async (modelId) => {
    const baseUrl = sanitizeBaseUrl(modelUrls[modelId]);
    if (!baseUrl) {
      setStatuses((prev) => ({
        ...prev,
        [modelId]: {
          ok: false,
          error: 'Base URL is empty',
          checkedAt: new Date().toISOString(),
        },
      }));
      return;
    }
    try {
      const response = await axios.get(`${baseUrl}/health`, { timeout: 5000 });
      setStatuses((prev) => ({
        ...prev,
        [modelId]: {
          ok: true,
          data: response.data,
          checkedAt: new Date().toISOString(),
        },
      }));
    } catch (err) {
      setStatuses((prev) => ({
        ...prev,
        [modelId]: {
          ok: false,
          error: err?.response?.data?.detail || err.message,
          checkedAt: new Date().toISOString(),
        },
      }));
    }
  };

  const checkAllStatuses = async () => {
    for (const model of MODEL_OPTIONS) {
      // Sequential to keep logs readable
      // eslint-disable-next-line no-await-in-loop
      await checkStatus(model.id);
    }
  };

  const handleRunMatching = async () => {
    if (!inputText.trim()) {
      setError('Zadejte alespoň jeden řádek poptávky.');
      return;
    }
    if (!activeBaseUrl) {
      setError('Vybraný model nemá nakonfigurovanou základní URL.');
      return;
    }

    const parsed = parseDemandLines(inputText);
    if (!parsed.length) {
      setError('Po zpracování nezůstal žádný validní řádek.');
      return;
    }

    setLoading(true);
    setError(null);
    setLines([]);
    setManualOverrides([]);
    setFinalizeMessage('');
    setProgressCount({ total: parsed.length, completed: 0 });

    try {
      const responses = [];

      if (parsed.length > 1 && !useLLM) {
        const payload = parsed.map((item) => item.query);
        const res = await axios.post(`${activeBaseUrl}/match_batch`, { texts: payload, k: 4 });
        res.data.forEach((item, index) => {
          const source = parsed[index];
          responses.push({
            raw: source.raw,
            quantity: source.quantity,
            query: source.query,
            best: item.best || null,
            alternatives: (item.alternatives || []).slice(0, 3),
            verified: null,
          });
          setProgressCount((prev) => ({ total: parsed.length, completed: Math.min(parsed.length, prev.completed + 1) }));
        });
      } else {
        for (const source of parsed) {
          // eslint-disable-next-line no-await-in-loop
          const res = await axios.post(`${activeBaseUrl}/match`, {
            text: source.query,
            k: 4,
            verify: useLLM,
          });
          responses.push({
            raw: source.raw,
            quantity: source.quantity,
            query: source.query,
            best: res.data.best || null,
            alternatives: (res.data.alternatives || []).slice(0, 3),
            verified: res.data.verified || null,
          });
          setProgressCount((prev) => ({ total: parsed.length, completed: Math.min(parsed.length, prev.completed + 1) }));
        }
      }

      const enriched = responses.map((item, index) => ({
        id: `line-${Date.now()}-${index}`,
        rawInput: item.raw,
        quantity: item.quantity,
        query: item.query,
        best: item.best,
        alternatives: item.alternatives,
        verified: item.verified,
        selection: null,
        status: 'pending',
        manualDraft: {
          sap: '',
          notes: '',
        },
        manualError: '',
      }));

      setLines(enriched);
      setActiveLineId(enriched[0]?.id || null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Chyba při volání API.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTestData = () => {
    const generated = getRandomDemandLines();
    if (!generated.length) {
      setError('Testovací dataset není k dispozici. Spusťte skript generate_test_queries.');
      return;
    }
    setError(null);
    setFinalizeMessage('');
    setInputText(generated.join('\n'));
    setLines([]);
    setManualOverrides([]);
    setActiveLineId(null);
  };

  const handleSlangFieldChange = (field, value) => {
    setSlangForm((prev) => ({ ...prev, [field]: value }));
    setSlangStatus({ message: '', error: false });
  };

  const handleSlangSubmit = async () => {
    const slangValue = slangForm.slang.trim();
    const canonicalValue = slangForm.canonical.trim();
    if (!slangValue || !canonicalValue) {
      setSlangStatus({ message: 'Vyplňte slang i správný výraz.', error: true });
      return;
    }
    if (!activeBaseUrl) {
      setSlangStatus({ message: 'Nejprve zvolte model a zadejte platnou URL.', error: true });
      return;
    }

    setSlangSubmitting(true);
    setSlangStatus({ message: '', error: false });
    try {
      await axios.post(`${activeBaseUrl}/slang_pairs`, {
        slang: slangValue,
        canonical: canonicalValue,
        notes: slangForm.notes.trim() || undefined,
        submitted_by: selectedModelId,
      });
      setSlangStatus({ message: 'Slangová dvojice byla uložena do sdíleného slovníku.', error: false });
      setSlangForm({ slang: '', canonical: '', notes: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Chyba při ukládání slangové dvojice.';
      setSlangStatus({ message: detail, error: true });
    } finally {
      setSlangSubmitting(false);
    }
  };

  const handleSelectCandidate = (lineId, candidate, originLabel) => {
    let nextActiveId = null;
    setLines((prev) => {
      const updated = prev.map((line) => {
        if (line.id !== lineId) {
          return line;
        }
        return {
          ...line,
          selection: {
            type: 'candidate',
            origin: originLabel,
            sap: candidate?.sap || '',
            data: candidate,
            chosenAt: new Date().toISOString(),
          },
          status: 'ready',
          manualDraft: {
            sap: line.manualDraft.sap,
            notes: line.manualDraft.notes,
          },
          manualError: '',
        };
      });
      nextActiveId = findNextLineId(updated, lineId);
      const doneCount = updated.filter((item) => item.selection).length;
      setProgressCount((prev) => ({ total: prev.total || updated.length, completed: doneCount }));
      return updated;
    });
    setManualOverrides((prev) => prev.filter((entry) => entry.lineId !== lineId));
    if (nextActiveId) {
      setActiveLineId(nextActiveId);
    }
    setFinalizeMessage('');
  };

  const handleManualDraftChange = (lineId, field, value) => {
    setLines((prev) =>
      prev.map((line) =>
        line.id === lineId
          ? {
              ...line,
              manualDraft: {
                ...line.manualDraft,
                [field]: value,
              },
            }
          : line
      )
    );
  };

  const handleManualConfirm = (lineId) => {
    const line = lines.find((item) => item.id === lineId);
    if (!line) {
      return;
    }
    const sapCode = (line.manualDraft.sap || '').trim();
    if (!sapCode) {
      setLines((prev) =>
        prev.map((item) =>
          item.id === lineId
            ? {
                ...item,
                manualError: 'Zadejte platný SAP kód.',
              }
            : item
        )
      );
      return;
    }

    const notes = (line.manualDraft.notes || '').trim();
    const manualRecord = {
      lineId,
      timestamp: new Date().toISOString(),
      inputRow: line.rawInput,
      quantity: line.quantity,
      modelId: selectedModelId,
      modelUrl: activeBaseUrl,
      best: line.best,
      alternatives: line.alternatives,
      manualSap: sapCode,
      notes,
    };

    let nextActiveId = null;
    setLines((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== lineId) {
          return item;
        }
        return {
          ...item,
          selection: {
            type: 'manual',
            origin: 'manual',
            sap: sapCode,
            notes,
            chosenAt: manualRecord.timestamp,
          },
          status: 'manual',
          manualDraft: {
            sap: sapCode,
            notes,
          },
          manualError: '',
        };
      });
      nextActiveId = findNextLineId(updated, lineId);
      return updated;
    });

    setManualOverrides((prev) => {
      const others = prev.filter((entry) => entry.lineId !== lineId);
      return [...others, manualRecord];
    });

    if (nextActiveId) {
      setActiveLineId(nextActiveId);
    }
    setFinalizeMessage('');
  };

  const handleResetLine = (lineId) => {
    setLines((prev) => {
      const updated = prev.map((line) =>
        line.id === lineId
          ? {
              ...line,
              selection: null,
              status: 'pending',
              manualDraft: {
                sap: '',
                notes: '',
              },
              manualError: '',
            }
          : line
      );
      const remaining = updated.filter((item) => item.selection).length;
      setProgressCount((prev) => ({ total: prev.total || updated.length, completed: remaining }));
      return updated;
    });
    setManualOverrides((prev) => prev.filter((entry) => entry.lineId !== lineId));
    setFinalizeMessage('');
  };

  const selectionsComplete = lines.length > 0 && lines.every((line) => line.selection);
  const resolvedCount = progressCount.completed;
  const progressPct = progressCount.total ? Math.round((resolvedCount / progressCount.total) * 100) : 0;

  const handleFinalize = async () => {
    if (!lines.length) {
      setError('Nejprve zpracujte poptávku.');
      return;
    }
    const unresolved = lines.filter((line) => !line.selection);
    if (unresolved.length) {
      const nextId = unresolved[0].id;
      setActiveLineId(nextId);
      setError('Dokončete výběr u všech řádků před finálním exportem.');
      return;
    }
    if (!activeBaseUrl) {
      setError('Vybraný model nemá nakonfigurovanou základní URL.');
      return;
    }

    setError(null);
    setFinalizeMessage('');
    setFinalizing(true);

    const now = new Date();
    const tag = formatDateTag(now);

    const successColumns = [
      { header: 'timestamp', accessor: () => now.toISOString() },
      { header: 'model_id', accessor: () => selectedModelId },
      { header: 'model_url', accessor: () => activeBaseUrl },
      { header: 'input_row', accessor: (row) => row.rawInput },
      { header: 'quantity', accessor: (row) => row.quantity },
      { header: 'selected_source', accessor: (row) => row.selection?.origin || '' },
      { header: 'selected_sap', accessor: (row) => row.selection?.sap || '' },
      {
        header: 'selected_title',
        accessor: (row) => (row.selection?.type === 'candidate' ? row.selection.data?.name || '' : 'Manuální volba'),
      },
      {
        header: 'selected_confidence_pct',
        accessor: (row) => {
          if (row.selection?.type !== 'candidate') {
            return '';
          }
          const confidence = computeCandidateConfidence(row.selection.data);
          return confidence !== null ? String(confidence) : '';
        },
      },
      {
        header: 'selected_ean',
        accessor: (row) => (row.selection?.type === 'candidate' ? row.selection.data?.ean || '' : ''),
      },
    ];

    const sheetPayload = {
      model_id: selectedModelId,
      model_url: activeBaseUrl,
      lines: lines.map((row) => {
        const selection = row.selection || {};
        const sapCode = selection.sap || '';
        const candidateName = selection.type === 'candidate' ? selection.data?.name || '' : '';
        const verifiedLabel = selection.type === 'candidate'
          ? [sapCode, candidateName].filter(Boolean).join(' — ')
          : sapCode;
        return {
          input_row: row.rawInput,
          verified_output: verifiedLabel,
          sap: sapCode,
          name: selection.type === 'candidate' ? candidateName : selection.notes || '',
          ean: selection.type === 'candidate' ? selection.data?.ean || '' : '',
          quantity: row.quantity,
          selection_type: selection.type || '',
          source: selection.origin || '',
          notes: selection.type === 'manual' ? selection.notes || row.manualDraft.notes || '' : '',
        };
      }),
    };

    try {
      await axios.post(`${activeBaseUrl}/verified_matches`, sheetPayload);

      const successCsv = buildCsv(successColumns, lines);
      triggerCsvDownload(`successfulOrder_${tag}.csv`, successCsv);

      setFinalizeMessage('Export úspěšný. Data byla uložena do Google Sheets a soubor byl stažen.');
      setError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Chyba při ukládání potvrzených záznamů.';
      setError(`Nepodařilo se uložit potvrzené páry: ${detail}`);
      return;
    } finally {
      setFinalizing(false);
    }
  };

  const renderStatusBadge = (modelId) => {
    const status = statuses[modelId];
    if (!status) {
      return <span className="status-badge idle">Status neznámý</span>;
    }
    if (status.ok) {
      return <span className="status-badge ok">✓ Online</span>;
    }
    return <span className="status-badge error">× Offline</span>;
  };

  const activeLine = lines.find((line) => line.id === activeLineId) || lines[0];

  return (
    <div className="app-shell">
      <div className="app-container">
        <header className="brand-header">
          <div className="brand-identity">
            <img src={LogoFull} alt="EMAS" className="brand-logo" />
            <div className="brand-title">
              <div>EMAS Code Matching — Nabídka Builder</div>
              <span>
                Vyberte správný SAP kód pro každý řádek poptávky, nechte si poradit modelem a případně manuálně
                upravte.
              </span>
              <span className="brand-chip">
                <img src={MiniLogo} alt="EMAS symbol" />
                EMAS pilotní release
              </span>
            </div>
          </div>
        </header>

        <section className="config-row">
          <div className="config-card">
            <div className="card-title">Model &amp; konektivita</div>
            <div className="card-subtitle">Vyberte backend, upravte URL a zkontrolujte stav.</div>
            <div className="model-options">
              {MODEL_OPTIONS.map((model) => (
                <div
                  key={model.id}
                  className={`model-option ${selectedModelId === model.id ? 'active' : ''}`}
                  onClick={() => setSelectedModelId(model.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      setSelectedModelId(model.id);
                    }
                  }}
                >
                  <div className="model-info">
                    <h4>{model.label}</h4>
                    <div className="model-url-input">
                      <label htmlFor={`url-${model.id}`}>Base URL</label>
                      <input
                        id={`url-${model.id}`}
                        value={modelUrls[model.id] || ''}
                        onChange={(event) => updateModelUrl(model.id, event.target.value)}
                        onClick={(event) => event.stopPropagation()}
                        placeholder={model.defaultUrl}
                      />
                    </div>
                    {statuses[model.id]?.data && (
                      <div className="model-flags">
                        <span
                          className={`model-flag ${
                            statuses[model.id].data.use_exact ? 'ok' : 'off'
                          }`}
                        >
                          Exact match {statuses[model.id].data.use_exact ? 'ON' : 'OFF'}
                        </span>
                        <span
                          className={`model-flag ${
                            statuses[model.id].data.use_code ? 'ok' : 'off'
                          }`}
                        >
                          Kódové vyhledání {statuses[model.id].data.use_code ? 'ON' : 'OFF'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="status-row">
                    {renderStatusBadge(model.id)}
                    <button
                      type="button"
                      className="btn secondary status-check-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        checkStatus(model.id);
                      }}
                    >
                      Check
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="config-card">
            <div className="card-title">LLM verifikace</div>
            <div className="card-subtitle">
              Zapněte, pokud chcete pro každý řádek spustit validaci přes LLM reranker (pomalejší, detailnější).
            </div>
            <div className="toggle">
              <input
                type="checkbox"
                id="llm-toggle"
                checked={useLLM}
                onChange={(event) => setUseLLM(event.target.checked)}
              />
              <label htmlFor="llm-toggle">
                Povolit LLM ověření shody a vysvětlení
              </label>
            </div>
            <p className="helper-text">
              Pokud zadáte více řádků a LLM je vypnuté, použije se rychlý batch endpoint bez verifikace.
            </p>
          </div>

          <div className="config-card">
            <div className="card-title">Slovníček slangových výrazů</div>
            <div className="card-subtitle">
              Zadejte, jak zákazníci slangově označují produkt, a přiřaďte správný oficiální název pro trénink slovníku.
            </div>
            <div className="slang-form">
              <label htmlFor="slang-term">Slangový výraz</label>
              <input
                id="slang-term"
                value={slangForm.slang}
                onChange={(event) => handleSlangFieldChange('slang', event.target.value)}
                placeholder="Např. Wédéčko"
              />
              <label htmlFor="canonical-term">Správný výraz / produkt</label>
              <input
                id="canonical-term"
                value={slangForm.canonical}
                onChange={(event) => handleSlangFieldChange('canonical', event.target.value)}
                placeholder="Např. WD-40 univerzální mazivo"
              />
              <label htmlFor="slang-notes">Poznámka (volitelné)</label>
              <textarea
                id="slang-notes"
                rows={2}
                value={slangForm.notes}
                onChange={(event) => handleSlangFieldChange('notes', event.target.value)}
                placeholder="Jaký kontext nebo zákazník výraz používá?"
              />
              <div className="slang-actions">
                <button type="button" className="btn" onClick={handleSlangSubmit} disabled={slangSubmitting}>
                  {slangSubmitting ? 'Ukládám…' : 'Přidat do slovníku'}
                </button>
                {slangStatus.message && (
                  <span className={slangStatus.error ? 'error-banner' : 'success-banner'}>
                    {slangStatus.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <hr className="section-divider" />

        <section className="text-input-card">
          <div className="card-title">Vstupní řádky poptávky</div>
          <textarea
            value={inputText}
            onChange={(event) => setInputText(event.target.value)}
            placeholder="Například: 12x svorka WAGO 221 3x 4mm2"
          />
          <div className="text-input-actions">
            <button type="button" className="btn" onClick={handleRunMatching} disabled={loading}>
              {loading ? 'Vyhledávání…' : 'Spustit párování'}
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={handleGenerateTestData}
              disabled={loading}
            >
              Generovat testovací data
            </button>
            <span className="helper-text">
              Systém vrátí top doporučení a tři alternativy pro každý řádek. Pro rychlé demo použijte generátor
              testovacích dat.
            </span>
            {activeBaseUrl ? (
              <span className="meta-hint">Aktivní endpoint: {activeBaseUrl}</span>
            ) : (
              <span className="meta-hint" style={{ color: '#7c2412' }}>
                Přidejte URL pro vybraný model, jinak se požadavky neodešlou.
              </span>
            )}
          </div>
        </section>

        {error && <div className="error-banner">{error}</div>}

        {lines.length > 0 && (
          <section className="progress-card">
            <div className="summary-row">
              <strong>Postup párování</strong>
              <span>
                Dokončeno {resolvedCount} / {progressCount.total}
              </span>
            </div>
            <div className="progress-bar">
              <span style={{ width: `${progressPct}%` }} />
            </div>
          </section>
        )}

        {lines.length > 0 && (
          <section className="workspace">
            <aside className="lines-panel">
              {lines.map((line, index) => (
                <div
                  key={line.id}
                  className={`line-item ${line.id === activeLineId ? 'active' : ''} ${
                    line.selection ? 'completed' : ''
                  }`}
                  onClick={() => setActiveLineId(line.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      setActiveLineId(line.id);
                    }
                  }}
                >
                  <div className="line-main">
                    {index + 1}. {line.rawInput}
                  </div>
                  <div className="line-secondary">
                    <span>Množství: {line.quantity}</span>
                    <span
                      className={`line-status ${
                        line.status === 'pending'
                          ? 'pending'
                          : line.status === 'manual'
                          ? 'manual'
                          : 'ready'
                      }`}
                    >
                      {line.status === 'pending' && 'čeká'}
                      {line.status === 'ready' && 'potvrzeno'}
                      {line.status === 'manual' && 'manuálně'}
                    </span>
                  </div>
                </div>
              ))}
            </aside>

            {activeLine ? (
              <div className="match-pane">
                <div className="match-header">
                  <div>
                    <h3>Řádek poptávky</h3>
                    <div className="match-input-preview">{activeLine.rawInput}</div>
                  </div>
                  {activeLine.selection && (
                    <div className="badge score">
                      {activeLine.selection.origin === 'manual'
                        ? 'Manuální volba'
                        : `Vybráno: ${activeLine.selection.origin}`}
                    </div>
                  )}
                </div>

                <div className="candidates-grid">
                  {activeLine.best ? (
                    <div
                      className={`candidate-card recommended ${
                        activeLine.selection?.sap === activeLine.best.sap && activeLine.selection?.type === 'candidate'
                          ? 'selected'
                          : ''
                      }`}
                    >
                      <div className="candidate-header">
                        <span className="candidate-label">Primární doporučení</span>
                        <div className="candidate-actions">
                          {buildCandidateBadges(activeLine.best).map((badge) => (
                            <span key={badge.key} className={badge.className}>
                              {badge.label}
                            </span>
                          ))}
                          <button
                            type="button"
                            className="btn secondary"
                            onClick={() => handleSelectCandidate(activeLine.id, activeLine.best, 'best')}
                          >
                            Potvrdit
                          </button>
                        </div>
                      </div>
                      <div className="candidate-body">
                        <span><strong>SAP:</strong> {activeLine.best.sap || '—'}</span>
                        <span><strong>EAN:</strong> {activeLine.best.ean || '—'}</span>
                        <span><strong>Název:</strong> {activeLine.best.name || '—'}</span>
                        {activeLine.best.detail && <span><strong>Detail:</strong> {activeLine.best.detail}</span>}
                      </div>
                    </div>
                  ) : (
                    <div className="candidate-card recommended">
                      <div className="candidate-body">Pro tento řádek nebyla nalezena žádná shoda. Zvažte manuální zadání.</div>
                    </div>
                  )}

                  {activeLine.alternatives?.map((candidate, idx) => (
                    <div
                      key={`${activeLine.id}-alt-${idx}`}
                      className={`candidate-card ${
                        activeLine.selection?.sap === candidate.sap && activeLine.selection?.type === 'candidate'
                          ? 'selected'
                          : ''
                      }`}
                    >
                      <div className="candidate-header">
                        <span className="candidate-label">Alternativa {idx + 1}</span>
                        <div className="candidate-actions">
                          {buildCandidateBadges(candidate).map((badge) => (
                            <span key={badge.key} className={badge.className}>
                              {badge.label}
                            </span>
                          ))}
                          <button
                            type="button"
                            className="btn secondary"
                            onClick={() => handleSelectCandidate(activeLine.id, candidate, `alt_${idx + 1}`)}
                          >
                            Vybrat
                          </button>
                        </div>
                      </div>
                      <div className="candidate-body">
                        <span><strong>SAP:</strong> {candidate.sap || '—'}</span>
                        <span><strong>EAN:</strong> {candidate.ean || '—'}</span>
                        <span><strong>Název:</strong> {candidate.name || '—'}</span>
                        {candidate.detail && <span><strong>Detail:</strong> {candidate.detail}</span>}
                      </div>
                    </div>
                  ))}

                  <div className="manual-card">
                    <h4>Neodpovídá nic? Zadejte SAP kód ručně.</h4>
                    <div className="manual-form">
                      <input
                        type="text"
                        value={activeLine.manualDraft.sap}
                        placeholder="SAP kód"
                        onChange={(event) => handleManualDraftChange(activeLine.id, 'sap', event.target.value)}
                      />
                      <textarea
                        rows={2}
                        value={activeLine.manualDraft.notes}
                        placeholder="Poznámka pro tým (volitelné)"
                        onChange={(event) => handleManualDraftChange(activeLine.id, 'notes', event.target.value)}
                      />
                      {activeLine.manualError && <span className="error-banner">{activeLine.manualError}</span>}
                      <div className="candidate-actions">
                        <button type="button" className="btn" onClick={() => handleManualConfirm(activeLine.id)}>
                          Potvrdit manuální SAP
                        </button>
                        {activeLine.selection && (
                          <button
                            type="button"
                            className="btn secondary"
                            onClick={() => handleResetLine(activeLine.id)}
                          >
                            Vymazat volbu
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {useLLM && activeLine.verified && (
                  <div className="manual-card" style={{ marginTop: 20, borderStyle: 'solid' }}>
                    <h4>LLM ověření</h4>
                    <div className="candidate-body">
                      <span><strong>Model:</strong> {activeLine.verified.model || '—'}</span>
                      <span><strong>Rozhodnutí:</strong> {activeLine.verified.chosen_index ?? '—'}</span>
                      <span><strong>Zdůvodnění:</strong> {activeLine.verified.reason || activeLine.verified.error || '—'}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="match-pane">
                <p>Vyberte řádek zleva pro zobrazení detailu.</p>
              </div>
            )}
          </section>
        )}

        {lines.length > 0 && (
          <section className="summary-panel">
            <div className="summary-row">
              <strong>Souhrn výběru</strong>
              <span>
                Připraveno k exportu: {resolvedCount} / {lines.length}
              </span>
            </div>
            <div className="finalize-bar">
              {finalizeMessage && <div className="success-banner">{finalizeMessage}</div>}
              <button
                type="button"
                className="btn cta"
                disabled={!selectionsComplete || finalizing}
                onClick={handleFinalize}
              >
                {finalizing ? 'Ukládám…' : 'Finalizovat nabídku & stáhnout CSV'}
              </button>
            </div>
            {!selectionsComplete && (
              <span className="meta-hint">
                Dokončete volbu pro všechny řádky, aby bylo možné vygenerovat export.
              </span>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default App;
